import { AfterViewInit, Component, OnInit, OnDestroy, ViewChild, TemplateRef, inject, ChangeDetectorRef } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { Observable, Subject, of } from 'rxjs';
import { shareReplay, map, catchError, takeUntil, filter, take } from 'rxjs/operators';
import moment from 'moment-timezone';
import { TranslationService } from '../../../../shared/translations/services/translation.service';
import { ColumnPreferencesService } from '../../../../shared/services/column-preferences.service';
import { ColumnPreferencesHelperService } from '../../../../shared/services/column-preferences-helper.service';
import {
  CasingSearchState,
  UpdateSelectedCasingsAction,
  UpdateIsRestoreStateAction,
  UpdateSearchCriteriaAction
} from '../../../../shared/state/CasingSearchState';
import { PaginatedCasingSearchResponse } from '../../model/casing-search-response.model';
import { CasingSearchResult } from '../../model/casing-search-result.model';
import { CasingSearchRequest } from '../../model/casing-search-request.model';
import { CasingSearchService, TransferCasingsResponse } from '../../services/casing-search.services';
import { EnhancedCasingNavigationService } from '../../services/enhanced-casing-navigation.service';
import { TableConfig, PaginatorFetcher, PaginatorFilters, AlertType, Alert, AlertService, ModalComponent } from '@michelin/theme';
import { TranslationState } from '../../../../shared/state/TranslationState';
import { FEATURE_FLAGS } from '../../../../shared/constants/constants';
import { isDeletableCasingStatus } from '../../../workorder/shared/util/work-order-helper-util';
import { FeatureflagsService } from '../../../../shared/services/featureflags.service';
import { WorkOrderService } from '../../../workorder/services/workorder.service';
import { SharedDeleteModalComponent } from '../../../../shared/components/shared-delete-modal/shared-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { ExportColumnDef } from '../../../../shared/models/export-column-def.model';
import { ExportModalComponent, ExportModalData } from '../../../../shared/components/export-modal/export-modal.component';
import { ExportResponse } from '../../../../shared/services/export-to-excel.service';
import { Dealer } from '../../../../shared/dealer/models/dealer';
import { DealerState } from '../../../../shared/state/DealerState';
import { UserState } from '../../../../shared/state/UserState';
import { environment } from 'src/environments/environment';
import { CustomDateAdapter } from '../../../../shared/services/custom-date-adapter';
import { PaginationState, PaginationStateService } from '../../services/pagination-state.service';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { AutocompleteOption } from '../../model/casing-auto-complete.model';
import { CasingDropdownDataState } from '../../../../shared/state/CasingDropdownState';

export interface CasingFilterTag {
  id: string;
  label: string;
  value: string;
  /** Criteria keys cleared when the tag is removed. */
  keys: (keyof CasingSearchRequest)[];
  /** Single array entry removed when set; otherwise all keys are cleared. */
  removeValue?: string;
}

const MULTI_SELECT_KEYS = [
  'casingMakeId',
  'casingSeriesId',
  'casingSizeId',
  'treadDescId',
  'treadWidthId',
  'casingGrade',
  'casingStatus',
  'shippingLocation',
  'postMarker',
  'siteFk',
  'processCode'
] as const;

/** Wraps legacy string values of multi-select filters in an array; arrays pass through. */
function normalizeMultiSelectCriteria(criteria: CasingSearchRequest): CasingSearchRequest {
  const normalized: CasingSearchRequest = { ...criteria };
  MULTI_SELECT_KEYS.forEach((key) => {
    const current = normalized[key] as string[] | string | undefined;
    if (typeof current === 'string') {
      normalized[key] = [current];
    }
  });
  return normalized;
}

@Component({
  selector: 'app-casing-search-result',
  templateUrl: './casing-search-result.component.html',
  styleUrls: ['./casing-search-result.component.scss'],
  standalone: false
})
export class CasingSearchResultComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly POS_EXPORT_WAITING_MESSAGE = 'POS Export is still in WAITING status';
  @ViewChild('paginatedTable', { static: false }) tableRef!: any;
  @ViewChild('transferValidationModal', { static: false }) transferValidationModal!: ModalComponent;
  @ViewChild('transferConfirmationModal', { static: false }) transferConfirmationModal!: ModalComponent;
  @ViewChild('transferConfirmationContent', { static: false }) transferConfirmationContent!: TemplateRef<any>;
  @ViewChild('transferValidationContent', { static: false }) transferValidationContent!: TemplateRef<any>;
  dealer: Dealer;
  currentUserName: string;

  public store = inject(Store);
  public router = inject(Router);
  public dialog = inject(MatDialog);
  public alertService = inject(AlertService);
  public casingSearchService = inject(CasingSearchService);
  public enhancedCasingNavigationService = inject(EnhancedCasingNavigationService);
  public translationService = inject(TranslationService);
  public featureflagsService = inject(FeatureflagsService);
  public workOrderService = inject(WorkOrderService);
  public dateAdapter = inject(CustomDateAdapter);
  public columnPrefService = inject(ColumnPreferencesService);
  public cdr = inject(ChangeDetectorRef);
  public columnPrefsHelper = inject(ColumnPreferencesHelperService);
  public paginationStateService = inject(PaginationStateService);

  searchResults$: Observable<PaginatedCasingSearchResponse | null>;
  selectedCasings$: Observable<CasingSearchResult[]>;
  searchCriteria$: Observable<CasingSearchRequest | null>;
  filterTags$: Observable<CasingFilterTag[]>;

  private readonly cachedPages: Map<string, CasingSearchResult[]> = new Map();
  private totalRecords: number = 0;
  // Current paginator page (1-based) used for restore/retain behavior.
  private currentPage = 1;
  private currentPageSize: number = 10;
  // Must match the table fetch default (creationDate DESC) and the initial search's backend default sort,
  // otherwise the page-1 cache seeded from the search is missed and the same query runs twice.
  private currentSortField: string = 'creationDate';
  private currentSortDirection: string = 'DESC';
  private visibleColumnKeys: string[] | null = null;
  // mic-table localStorage namespace for this screen's paginator state.
  private readonly paginationStoragePrefix = 'casing-search-result-';
  // Captures the outbound route to decide whether to retain or clear pagination.
  private nextNavigationUrl = '';
  public isDeletableCasingStatus = isDeletableCasingStatus;
  // column selector state
  private canonicalColumns: Record<string, any> = {};
  columnKeys: string[] = [];
  columnNames: Record<string, string> = {};
  columnOrder: string[] = [];
  columnVisibility: Record<string, boolean> = {};

  // Loading states for navigation
  loadingCasingId: string | null = null;
  loadingWorkOrderNum: string | null = null;
  isDeleteWorkOrderEnabled = false;
  showTransferButton = false;
  transferValidationMessages: string[] = [];
  isRSCasingTransferEnabled = false;

  private readonly destroy$ = new Subject<void>();

  protected readonly environment = environment;

  constructor() {
    this.searchResults$ = this.store.select(CasingSearchState.searchResults);
    this.selectedCasings$ = this.store.select(CasingSearchState.selectedCasings);
    this.searchCriteria$ = this.store.select(CasingSearchState.searchCriteria);
    this.filterTags$ = this.searchCriteria$.pipe(map((criteria) => this.buildFilterTags(criteria)));
  }

  private buildFilterTags(criteria: CasingSearchRequest | null): CasingFilterTag[] {
    if (!criteria) {
      return [];
    }
    const t = (key: string) => this.translationService.translate(key);
    const tags: CasingFilterTag[] = [];
    const add = (label: string, value: unknown, keys: (keyof CasingSearchRequest)[]) => {
      if (value === undefined || value === null || value === '') {
        return;
      }
      tags.push({ id: keys.join('|'), label, value: String(value), keys });
    };
    const addRange = (label: string, start: string | undefined, end: string | undefined, keys: (keyof CasingSearchRequest)[]) => {
      if (!start && !end) {
        return;
      }
      const value = start && end ? `${this.formatTagDate(start)} - ${this.formatTagDate(end)}` : this.formatTagDate(start || end);
      add(label, value, keys);
    };

    add(t('barCodeNumberText'), criteria.casingId, ['casingId']);

    const normalized = normalizeMultiSelectCriteria(criteria);
    const addMulti = (
      label: string,
      key: (typeof MULTI_SELECT_KEYS)[number],
      options: AutocompleteOption[],
      toLabel: (option: AutocompleteOption) => string
    ) => {
      (normalized[key] ?? []).forEach((id) => {
        const found = options?.find((option) => option.id === id);
        if (found) {
          tags.push({ id: `${key}|${id}`, label, value: toLabel(found), keys: [key], removeValue: id });
        }
      });
    };
    addMulti(
      t('makeText'),
      'casingMakeId',
      this.store.selectSnapshot(CasingDropdownDataState.makes),
      (o) => `${t(o.code)} - ${t(o.description)}`
    );
    addMulti(t('seriesText'), 'casingSeriesId', this.store.selectSnapshot(CasingDropdownDataState.series), (o) => t(o.code));
    addMulti(t('sizeText'), 'casingSizeId', this.store.selectSnapshot(CasingDropdownDataState.sizes), (o) => t(o.code));
    addMulti(t('treadText'), 'treadDescId', this.store.selectSnapshot(CasingDropdownDataState.treads), (o) => t(o.code));
    addMulti(t('treadWidthText'), 'treadWidthId', this.store.selectSnapshot(CasingDropdownDataState.treadWidths), (o) => t(o.code));
    (normalized.casingGrade ?? []).forEach((grade) => {
      tags.push({ id: `casingGrade|${grade}`, label: t('casingGradeText'), value: grade, keys: ['casingGrade'], removeValue: grade });
    });
    add(t('mdotText'), criteria.mfgDot, ['mfgDot']);
    add(t('brandText'), criteria.brandNum, ['brandNum']);
    add(t('ntrText'), criteria.ntr, ['ntr']);
    const circumMin = criteria.buffCircumMin;
    const circumMax = criteria.buffCircumMax;
    const hasMin = circumMin !== undefined && circumMin !== null;
    const hasMax = circumMax !== undefined && circumMax !== null;
    const circumValue =
      hasMin && hasMax ? `${circumMin} – ${circumMax}` : hasMin ? `≥ ${circumMin}` : hasMax ? `≤ ${circumMax}` : undefined;
    add(t('circumferenceText'), circumValue, ['buffCircumMin', 'buffCircumMax']);
    const addValues = (label: string, key: (typeof MULTI_SELECT_KEYS)[number], toLabel: (value: string) => string = (v) => v) => {
      (normalized[key] ?? []).forEach((value) => {
        tags.push({ id: `${key}|${value}`, label, value: toLabel(value), keys: [key], removeValue: value });
      });
    };
    addValues(t('statusText'), 'casingStatus', (v) => this.getStatusLabel(v));
    addValues(t('casingLocationText'), 'shippingLocation');
    addValues(t('postText'), 'postMarker', (v) => v.replace(/_/g, ' '));
    addValues(t('siteText'), 'siteFk');
    addValues(t('processCodeText'), 'processCode');
    add(t('sequenceNumberText'), criteria.sequenceNum, ['sequenceNum']);
    add(t('customerText'), criteria.customerCode, ['customerCode', 'customerFks']);
    addRange(t('receivedDateText'), criteria.casingReceivedDateStart, criteria.casingReceivedDateEnd, [
      'casingReceivedDateStart',
      'casingReceivedDateEnd'
    ]);
    addRange(t('dueDateText'), criteria.woDueDateStart, criteria.woDueDateEnd, ['woDueDateStart', 'woDueDateEnd']);
    addRange(t('creationDateText'), criteria.woCreatedDateStart, criteria.woCreatedDateEnd, ['woCreatedDateStart', 'woCreatedDateEnd']);
    addRange(t('completeDateText'), criteria.casingCompleteDateStart, criteria.casingCompleteDateEnd, [
      'casingCompleteDateStart',
      'casingCompleteDateEnd'
    ]);
    return tags;
  }

  private formatTagDate(value: string | undefined): string {
    if (!value) {
      return '';
    }
    const parsed = moment(value, moment.ISO_8601, true);
    return parsed.isValid() ? this.dateAdapter.format(parsed.toDate(), this.dateAdapter['currentFormat'] || 'DD/MM/YYYY') : value;
  }

  /** Removes a filter from the stored criteria and refetches the casing list from page 1. */
  removeFilter(tag: CasingFilterTag): void {
    const criteria = this.store.selectSnapshot(CasingSearchState.searchCriteria);
    if (!criteria) {
      return;
    }
    const updated: CasingSearchRequest = normalizeMultiSelectCriteria(criteria);
    if (tag.removeValue !== undefined) {
      const key = tag.keys[0] as (typeof MULTI_SELECT_KEYS)[number];
      const remaining = (updated[key] ?? []).filter((id) => id !== tag.removeValue);
      if (remaining.length) {
        updated[key] = remaining;
      } else {
        delete updated[key];
      }
    } else {
      tag.keys.forEach((key) => delete updated[key]);
    }
    this.store.dispatch(new UpdateSearchCriteriaAction(updated));
    this.cachedPages.clear();
    this.tableRef?.resetFilters();
  }

  ngOnInit(): void {
    this.isDeleteWorkOrderEnabled = this.featureflagsService.getFeatureFlagValue(FEATURE_FLAGS.DELETE_WORK_ORDER_ENABLED);
    this.isRSCasingTransferEnabled = this.featureflagsService.getFeatureFlagValue(FEATURE_FLAGS.RS_CASING_TRANSFER_ENABLED);
    this.dealer = this.store.selectSnapshot(DealerState.currentDealer);
    this.currentUserName = this.store.selectSnapshot(UserState.getUserName);
    this.searchCriteria$.pipe(takeUntil(this.destroy$)).subscribe((criteria) => {
      if (!criteria) {
        this.router.navigate(['/home/search-casings']);
      }
    });

    // Restore persisted pagination/sort metadata when coming back from detail pages.
    const savedPaginationState = this.paginationStateService.getPaginationState();
    if (savedPaginationState) {
      this.currentPageSize = savedPaginationState.pageSize || this.currentPageSize;
      this.currentPage = savedPaginationState.currentPage || 1;
      this.currentSortField = savedPaginationState.sortField || this.currentSortField;
      this.currentSortDirection = savedPaginationState.sortDirection || this.currentSortDirection;

      if (this.tableConfig.paginator) {
        this.tableConfig.paginator.pageSize = this.currentPageSize;
      }
      this.updateColumnSelectorPosition(this.currentPageSize);
    }

    // Track destination route so ngOnDestroy can enforce retain-vs-clear rules.
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationStart),
        takeUntil(this.destroy$)
      )
      .subscribe((event) => {
        this.nextNavigationUrl = (event as NavigationStart).url;
      });

    this.searchResults$.pipe(takeUntil(this.destroy$)).subscribe((results) => {
      if (results?.casingSearchResults) {
        const defaultPageSize = this.tableConfig.paginator?.pageSize || 10;
        this.currentPageSize = defaultPageSize;
        this.showTransferButton = this.hasTransferEligibleResults(results.casingSearchResults);
        if (this.casingSearchService.justSearched) {
          const cacheKey = this.getCacheKey(1, defaultPageSize, this.currentSortField, this.currentSortDirection);
          this.cachedPages.set(cacheKey, results.casingSearchResults);
          this.totalRecords = results.pagination?.totalRecords || results.casingSearchResults.length;
          this.casingSearchService.justSearched = false;
        }
      } else {
        this.showTransferButton = false;
      }
    });
    this.store.select(TranslationState.currentLanguage).subscribe(() => {
      // build canonical columns with translated headers
      this.canonicalColumns = {
        casingId: {
          cell: 'casingIdCell',
          header: this.translationService.translate('barCodeNumberText'),
          sortable: true
        },
        workOrderNum: {
          cell: 'workOrderNumCell',
          header: this.translationService.translate('workOrderNumberText'),
          sortable: true
        },
        customer: {
          cell: (item: CasingSearchResult) => item.customer || '-',
          header: this.translationService.translate('customerText'),
          sortable: true
        },
        customerName: {
          cell: (item: CasingSearchResult) => item.customerName || '-',
          header: `${this.translationService.translate('customerText')} ${this.translationService.translate('nameText')}`,
          sortable: true
        },
        productionCustomer: {
          cell: (item: CasingSearchResult) => item.productionCustomer || '-',
          header: this.translationService.translate('productionCustomerText'),
          sortable: true
        },
        productionCustomerName: {
          cell: (item: CasingSearchResult) => item.productionCustomerName || '-',
          header: `${this.translationService.translate('productionCustomerText')} ${this.translationService.translate('nameText')}`,
          sortable: true
        },
        casingStatus: {
          cell: 'casingStatusCell',
          header: this.translationService.translate('statusText'),
          sortable: true
        },
        processCode: {
          cell: (item: CasingSearchResult) => item.processCode || '-',
          header: this.translationService.translate('processCodeText'),
          sortable: true
        },
        shippingLocation: {
          cell: (item: CasingSearchResult) => item.shippingLocation || '-',
          header: this.translationService.translate('casingLocationText'),
          sortable: true
        },
        postMarker: {
          cell: (item: CasingSearchResult) => item.postMarker || '-',
          header: this.translationService.translate('postText'),
          sortable: true
        },
        wosite: {
          cell: (item: CasingSearchResult) => item.wosite || '-',
          header: this.translationService.translate('siteText'),
          sortable: true
        },
        casingMake: {
          cell: (item: CasingSearchResult) => item.casingMake || '-',
          header: this.translationService.translate('makeText'),
          sortable: true
        },
        casingSeries: {
          cell: (item: CasingSearchResult) => item.casingSeries || '-',
          header: this.translationService.translate('seriesText'),
          sortable: true
        },
        casingSize: {
          cell: (item: CasingSearchResult) => item.casingSize || '-',
          header: this.translationService.translate('sizeText'),
          sortable: true
        },
        treadDesc: {
          cell: (item: CasingSearchResult) => item.treadDesc || '-',
          header: this.translationService.translate('treadText'),
          sortable: true
        },
        ntr: {
          cell: (item: CasingSearchResult) => (item.ntr !== undefined && item.ntr !== null ? item.ntr.toString() : '0'),
          header: this.translationService.translate('ntrText'),
          sortable: true
        },
        mfgDot: {
          cell: (item: CasingSearchResult) => item.mfgDot || '-',
          header: this.translationService.translate('mdotText'),
          sortable: true
        },
        creationDate: {
          cell: 'creationDateCell',
          header: this.translationService.translate('creationDateText'),
          sortable: true
        },
        workOrderDueDate: {
          cell: (item: CasingSearchResult) => this.formatCreationDate(item.workOrderDueDate),
          header: this.translationService.translate('workOrderDueDateText'),
          sortable: true
        },
        workOrderCreationDate: {
          cell: (item: CasingSearchResult) => this.formatCreationDate(item.workOrderCreationDate),
          header: this.translationService.translate('workOrderCreationDateText'),
          sortable: true
        },
        sequenceNum: {
          cell: 'sequenceNumCell',
          header: this.translationService.translate('sequenceNumberText'),
          sortable: true
        },
        lineNum: {
          cell: (item: CasingSearchResult) => (item.lineNum !== undefined && item.lineNum !== null ? item.lineNum.toString() : '-'),
          header: this.translationService.translate('lineNumberText'),
          sortable: true
        },
        treadWidth: {
          cell: (item: CasingSearchResult) =>
            item.treadWidth !== undefined && item.treadWidth !== null ? item.treadWidth.toString() : '-',
          header: this.translationService.translate('treadWidthText'),
          sortable: true
        },
        scrapCode: {
          cell: (item: CasingSearchResult) => item.scrapCode || '-',
          header: this.translationService.translate('scrapCodeText'),
          sortable: true
        },
        rejectReason: {
          cell: (item: CasingSearchResult) => item.rejectReason || '-',
          header: this.translationService.translate('rejectReasonText'),
          sortable: true
        },
        disposalChargesCode: {
          cell: (item: CasingSearchResult) => item.disposalChargesCode || '-',
          header: this.translationService.translate('disposalChargeText'),
          sortable: true
        },
        // --- New Show/Hide Data fields (AC6) ---
        application: {
          cell: (item: CasingSearchResult) => item.application || '-',
          header: this.translationService.translate('applicationText'),
          sortable: true
        },
        axle: {
          cell: (item: CasingSearchResult) => item.axle || '-',
          header: this.translationService.translate('axleText'),
          sortable: true
        },
        rtDot: {
          cell: (item: CasingSearchResult) => item.rtDot || '-',
          header: this.translationService.translate('rtDotLabelText'),
          sortable: true
        },
        brandNum: {
          cell: (item: CasingSearchResult) => item.brandNum || '-',
          header: this.translationService.translate('brandText'),
          sortable: true
        },
        casingGrade: {
          cell: (item: CasingSearchResult) => (item.casingGrade ? item.casingGrade : item.grading) || '-',
          header: this.translationService.translate('casingGradeText'),
          sortable: true
        },
        crn: {
          cell: (item: CasingSearchResult) => item.crn || '-',
          header: this.translationService.translate('CRNText'),
          sortable: true
        },
        casingReceivedDate: {
          cell: (item: CasingSearchResult) => this.formatCreationDate(item.receivedDate),
          header: this.translationService.translate('receivedDateText'),
          sortable: true
        },
        workOrderHeaderStatus: {
          cell: (item: CasingSearchResult) => item.workOrderHeaderStatus || '-',
          header: this.translationService.translate('workOrderHeaderStatusText'),
          sortable: true
        },
        completeDate: {
          cell: (item: CasingSearchResult) => this.formatCreationDate(item.completeDate),
          header: this.translationService.translate('completeDateText'),
          sortable: true
        },
        currentSite: {
          cell: (item: CasingSearchResult) => item.currentSite || '-',
          header: this.translationService.translate('currentSiteText'),
          sortable: true
        },
        rejectCode: {
          cell: (item: CasingSearchResult) => item.rejectCode || '-',
          header: this.translationService.translate('rejectCodeText'),
          sortable: true
        },
        rejectDate: {
          cell: (item: CasingSearchResult) => this.formatCreationDate(item.rejectDate),
          header: this.translationService.translate('rejectDateText'),
          sortable: true
        },
        rejectPartNumber: {
          cell: (item: CasingSearchResult) => item.rejectPartNumber || '-',
          header: this.translationService.translate('rejectPartNumberText'),
          sortable: true
        },
        disposalChargeDescription: {
          cell: (item: CasingSearchResult) => item.disposalChargeDescription || '-',
          header: this.translationService.translate('disposalChargeDescriptionText'),
          sortable: true
        },
        scrapDescription: {
          cell: (item: CasingSearchResult) => item.scrapDescription || '-',
          header: this.translationService.translate('scrapDescriptionText'),
          sortable: true
        },
        invoiceDate: {
          cell: (item: CasingSearchResult) => this.formatCreationDate(item.invoiceDate),
          header: this.translationService.translate('invoiceDateText'),
          sortable: true
        },
        invoiceNumber: {
          cell: (item: CasingSearchResult) => item.invoiceNumber || '-',
          header: this.translationService.translate('invoiceNumberText'),
          sortable: true
        },
        packListId: {
          cell: (item: CasingSearchResult) => item.packListId || '-',
          header: this.translationService.translate('packListIdText'),
          sortable: true
        },
        finishedProductTread: {
          cell: (item: CasingSearchResult) => item.finishedProductTread || '-',
          header: this.translationService.translate('finishedProductTread'),
          sortable: true
        }
      };

      this.columnKeys = Object.keys(this.canonicalColumns);
      this.columnNames = this.columnKeys.reduce((acc, k) => {
        acc[k] = this.canonicalColumns[k]?.header || k;
        return acc;
      }, {} as Record<string, string>);

      const defaultVisibleColumns = new Set([
        'casingId',
        'workOrderNum',
        'lineNum',
        'customerName',
        'wosite',
        'casingStatus',
        'processCode',
        'shippingLocation',
        'postMarker',
        'application',
        'axle',
        'casingMake',
        'casingSeries',
        'casingSize',
        'treadDesc',
        'ntr',
        'mfgDot',
        'creationDate',
        'workOrderDueDate',
        'workOrderCreationDate',
        'casingReceivedDate'
      ]);
      const init = this.columnPrefsHelper.initialize('casing-search-result', this.canonicalColumns, {
        defaultHidden: Object.keys(this.canonicalColumns).filter((key) => !defaultVisibleColumns.has(key))
      });
      this.tableConfig.columns = init.tableColumns;
      this.columnNames = init.columnNames;
      this.columnKeys = init.columnKeys;
      this.columnOrder = init.columnOrder;
      this.columnVisibility = init.columnVisibility;
      this.cdr.markForCheck();
    });
  }

  ngAfterViewInit(): void {
    // mic-table may briefly show page 1 after data length updates; force one resync.
    this.tableRef?.paginator?.dataChanged.pipe(take(1), takeUntil(this.destroy$)).subscribe(() => {
      setTimeout(() => this.syncPaginatorPageIndicator());
    });
  }

  private syncPaginatorPageIndicator(): void {
    const paginator = this.tableRef?.paginator;
    const targetIndex = paginator?.pageIndex ?? 0;

    if (!paginator || !targetIndex) {
      return;
    }

    // Toggle page index on both paginator refs to refresh visual selected-page state.
    const paginatorRefs = [paginator.paginatorRef1, paginator.paginatorRef2].filter(Boolean);
    paginatorRefs.forEach((ref) => {
      ref.pageIndex = 0;
    });

    setTimeout(() => {
      paginatorRefs.forEach((ref) => {
        ref.pageIndex = targetIndex;
      });
    });
  }

  // Data Customization Control positioning (AC2-AC4): keep the column selector
  // and paginator in the same row when the controls are rendered above the table.
  private readonly SMALL_PAGE_SIZE_THRESHOLD = 10;

  private updateColumnSelectorPosition(pageSize: number): void {
    if (!this.tableConfig.paginator) {
      return;
    }
    const useBottomControls = pageSize <= this.SMALL_PAGE_SIZE_THRESHOLD;
    this.tableConfig.paginator.forceBottomAction = useBottomControls;
    this.tableConfig.paginator.position = useBottomControls ? 'bottom' : 'top';
  }

  tableConfig: TableConfig<CasingSearchResult> = {
    paginator: {
      // Align with packlist page-number style paginator behavior.
      style: 'pageNumber',
      options: {
        nbPagesToShow: 5
      },
      pageSize: 10,
      pageSizeOptions: [10, 20, 50, 100],
      forceBottomAction: true,
      position: 'bottom'
    },
    storage: {
      type: localStorage,
      options: {
        prefix: this.paginationStoragePrefix,
        keys: [],
        // Persist paginator metadata across navigation within allowed detail flow.
        paginator: true,
        columns: true,
        tags: false
      }
    },
    selection: {
      columnKeyForCheckboxLabel: 'casingId',
      allLabel: this.translationService.translate('allCasingsText')
    },
    onSelectionChange: (selectedItems) => {
      console.log('Selection change', selectedItems);
    },
    onColumnsChange: (columns: string[]) => {
      this.visibleColumnKeys = columns;
    },
    columns: {
      casingId: {
        cell: 'casingIdCell',
        header: this.translationService.translate('barCodeNumberText'),
        sortable: true
      },
      workOrderNum: {
        cell: 'workOrderNumCell',
        header: this.translationService.translate('workOrderNumberText'),
        sortable: true
      },
      customer: {
        cell: (item: CasingSearchResult) => item.customer || '-',
        header: this.translationService.translate('customerText'),
        sortable: true
      },
      customerName: {
        cell: (item: CasingSearchResult) => item.customerName || '-',
        header: `${this.translationService.translate('customerText')} ${this.translationService.translate('nameText')}`,
        sortable: true
      },
      productionCustomer: {
        cell: (item: CasingSearchResult) => item.productionCustomer || '-',
        header: this.translationService.translate('productionCustomerText'),
        sortable: true
      },
      productionCustomerName: {
        cell: (item: CasingSearchResult) => item.productionCustomerName || '-',
        header: `${this.translationService.translate('productionCustomerText')} ${this.translationService.translate('nameText')}`,
        sortable: true
      },
      casingStatus: {
        cell: 'casingStatusCell',
        header: this.translationService.translate('statusText'),
        sortable: true
      },
      processCode: {
        cell: (item: CasingSearchResult) => item.processCode || '-',
        header: this.translationService.translate('processCodeText'),
        sortable: true
      },
      shippingLocation: {
        cell: (item: CasingSearchResult) => item.shippingLocation || '-',
        header: this.translationService.translate('casingLocationText'),
        sortable: true
      },
      postMarker: {
        cell: (item: CasingSearchResult) => item.postMarker || '-',
        header: this.translationService.translate('postText'),
        sortable: true
      },
      wosite: {
        cell: (item: CasingSearchResult) => item.wosite || '-',
        header: this.translationService.translate('siteText'),
        sortable: true
      },
      casingMake: {
        cell: (item: CasingSearchResult) => item.casingMake || '-',
        header: this.translationService.translate('makeText'),
        sortable: true
      },
      casingSeries: {
        cell: (item: CasingSearchResult) => item.casingSeries || '-',
        header: this.translationService.translate('seriesText'),
        sortable: true
      },
      casingSize: {
        cell: (item: CasingSearchResult) => item.casingSize || '-',
        header: this.translationService.translate('sizeText'),
        sortable: true
      },
      treadDesc: {
        cell: (item: CasingSearchResult) => item.treadDesc || '-',
        header: this.translationService.translate('treadText'),
        sortable: true
      },
      ntr: {
        cell: (item: CasingSearchResult) => (item.ntr !== undefined && item.ntr !== null ? item.ntr.toString() : '0'),
        header: this.translationService.translate('ntrText'),
        sortable: true
      },
      mfgDot: {
        cell: (item: CasingSearchResult) => item.mfgDot || '-',
        header: this.translationService.translate('mdotText'),
        sortable: true
      },
      creationDate: {
        cell: 'creationDateCell',
        header: this.translationService.translate('creationDateText'),
        sortable: true
      },
      sequenceNum: {
        cell: (item: CasingSearchResult) =>
          item.sequenceNum !== undefined && item.sequenceNum !== null ? item.sequenceNum.toString() : '-',
        header: this.translationService.translate('sequenceNumberText'),
        sortable: true
      },
      lineNum: {
        cell: (item: CasingSearchResult) => (item.lineNum !== undefined && item.lineNum !== null ? item.lineNum.toString() : '-'),
        header: this.translationService.translate('lineNumberText'),
        sortable: true
      },
      treadWidth: {
        cell: (item: CasingSearchResult) => (item.treadWidth !== undefined && item.treadWidth !== null ? item.treadWidth.toString() : '-'),
        header: this.translationService.translate('treadWidthText'),
        sortable: true
      },
      scrapCode: {
        cell: (item: CasingSearchResult) => item.scrapCode || '-',
        header: this.translationService.translate('scrapCodeText'),
        sortable: true
      },
      rejectReason: {
        cell: (item: CasingSearchResult) => item.rejectReason || '-',
        header: this.translationService.translate('rejectReasonText'),
        sortable: true
      },
      disposalChargesCode: {
        cell: (item: CasingSearchResult) => item.disposalChargesCode || '-',
        header: this.translationService.translate('disposalChargeText'),
        sortable: true
      }
    }
  };

  // Toggle visibility for a column and persist preferences (delegates to helper)
  toggleColumn(key: string): void {
    const result = this.columnPrefsHelper.toggleAndSave(
      'casing-search-result',
      key,
      this.canonicalColumns,
      this.columnOrder,
      this.columnVisibility
    );
    this.tableConfig = {
      ...this.tableConfig,
      columns: result.tableColumns
    };
    this.columnVisibility = result.columnVisibility;
    this.cdr.markForCheck();
  }

  onColumnDrop(event: CdkDragDrop<string[]>): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }

    moveItemInArray(this.columnOrder, event.previousIndex, event.currentIndex);

    const columns: Record<string, any> = {};
    this.columnOrder.forEach((key) => {
      if (this.columnVisibility[key]) {
        columns[key] = this.canonicalColumns[key];
      }
    });

    this.tableConfig = {
      ...this.tableConfig,
      columns
    };
    this.columnPrefsHelper.save('casing-search-result', this.columnOrder, this.columnVisibility, this.canonicalColumns);
    this.cdr.markForCheck();
  }

  fetcher: PaginatorFetcher<CasingSearchResult> = (filters: PaginatorFilters) => {
    let sortField = 'creationDate';
    let sortDirection = 'DESC';
    if (filters.sort) {
      const [field, direction] = filters.sort.split(':');
      sortField = field || 'creationDate';
      sortDirection = direction || 'DESC';
    }

    const sortChanged = this.currentSortField !== sortField || this.currentSortDirection !== sortDirection;
    if (sortChanged) {
      this.cachedPages.clear();
      this.currentSortField = sortField;
      this.currentSortDirection = sortDirection;
    }

    // Keep in-memory page in sync with mic-table fetch cycle.
    this.currentPage = filters.page;

    const pageSizeChanged = this.currentPageSize !== filters.limit;
    if (pageSizeChanged) {
      this.cachedPages.clear();
      this.currentPageSize = filters.limit;
    }
    // Reposition the Show/Hide Data (column selector) button per AC2-AC4.
    this.updateColumnSelectorPosition(this.currentPageSize);

    // Save page/size/sort on each fetch trigger so latest state is always recoverable.
    this.savePaginationState();

    const cacheKey = this.getCacheKey(filters.page, filters.limit, sortField, sortDirection);
    const cachedData = this.cachedPages.get(cacheKey);
    if (cachedData && !sortChanged && !pageSizeChanged) {
      this.updateTransferButtonVisibility(cachedData);
      return of({
        items: cachedData,
        count: this.totalRecords
      });
    }

    const storedCriteria = this.store.selectSnapshot(CasingSearchState.searchCriteria);

    if (!storedCriteria) {
      this.updateTransferButtonVisibility([]);
      this.totalRecords = 0;
      this.router.navigate(['/home/search-casings']);
      return of({ items: [], count: 0 });
    }

    const searchRequest: CasingSearchRequest = {
      ...normalizeMultiSelectCriteria(storedCriteria),
      page: filters.page - 1,
      size: filters.limit,
      sortBy: sortField || 'casingId',
      sortDir: (sortDirection || 'ASC') as 'ASC' | 'DESC'
    };

    return this.casingSearchService.searchCasings(searchRequest).pipe(
      map((response: PaginatedCasingSearchResponse) => {
        const results = response.casingSearchResults || [];
        this.updateTransferButtonVisibility(results);

        if (response.casingSearchResults) {
          const responseCacheKey = this.getCacheKey(filters.page, filters.limit, sortField, sortDirection);
          this.cachedPages.set(responseCacheKey, response.casingSearchResults);
          this.totalRecords = response.pagination?.totalRecords || 0;
        }
        return {
          items: results,
          count: response.pagination?.totalRecords || 0
        };
      }),
      catchError((error) => {
        console.error('Casing search error:', error);
        this.updateTransferButtonVisibility([]);
        this.totalRecords = 0;
        return of({ items: [], count: 0 });
      }),
      shareReplay(1)
    );
  };
  ngOnDestroy(): void {
    // Persist one last snapshot before leaving this screen.
    this.savePaginationState();

    // Retain only for work-order detail routes; clear for search page or side-nav destinations.
    const isDetailNavigation = this.shouldRetainPaginationStateForNavigation(this.nextNavigationUrl);

    if (!isDetailNavigation) {
      this.paginationStateService.clearPaginationState();
      this.clearPaginationStorage();
    }

    this.store.dispatch(new UpdateIsRestoreStateAction(true));
    this.destroy$.next();
    this.destroy$.complete();
    this.cachedPages.clear();
  }

  private shouldRetainPaginationStateForNavigation(nextUrl: string): boolean {
    if (!nextUrl) {
      return false;
    }

    // Explicit allow-list: only details pages retain pagination on return.
    return nextUrl.startsWith('/home/work-orders/work-order-line-details') || nextUrl.startsWith('/home/work-orders/work-orders-details');
  }

  private clearPaginationStorage(): void {
    // Remove this screen's persisted paginator keys only.
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(this.paginationStoragePrefix)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  }

  private savePaginationState(selectedCasingId?: string): void {
    // Stores exact list context so user can resume from the same result position.
    const state: PaginationState = {
      currentPage: this.currentPage,
      pageSize: this.currentPageSize,
      sortField: this.currentSortField,
      sortDirection: this.currentSortDirection,
      totalRecords: this.totalRecords,
      selectedCasingId
    };
    this.paginationStateService.savePaginationState(state);
  }

  private getCacheKey(page: number, pageSize: number, sortField: string, sortDirection: string): string {
    return `${page}-${pageSize}-${sortField}-${sortDirection}`;
  }

  private updateTransferButtonVisibility(results?: CasingSearchResult[] | null): void {
    this.showTransferButton = this.hasTransferEligibleResults(results || []);
  }

  private hasTransferEligibleResults(results: CasingSearchResult[]): boolean {
    return results.some((result) => this.isTransferEligible(result));
  }

  private normalizeCasingStatus(status: string | undefined | null): string {
    return (status || '').trim().toUpperCase().replace(/\s+/g, '_');
  }

  private normalizeProcessCode(processCode: string | undefined | null): string {
    return (processCode || '').trim().toUpperCase();
  }

  private isTransferEligible(item: CasingSearchResult): boolean {
    return this.normalizeCasingStatus(item.casingStatus) === 'IN_STOCK' && this.normalizeProcessCode(item.processCode) === 'RS';
  }

  canTransferSelection(): boolean {
    const selectedItems: CasingSearchResult[] = this.tableRef?.selectedItems || [];
    return selectedItems.length > 0 && selectedItems.every((item) => this.isTransferEligible(item));
  }

  private hasOnlyTransferEligibleSelection(): boolean {
    const selectedItems: CasingSearchResult[] = this.tableRef?.selectedItems || [];
    return selectedItems.length > 0 && selectedItems.every((item) => this.isTransferEligible(item));
  }

  getTransferTooltipMessage(): string {
    return this.translationService.translate('transferTooltipMessageText');
  }

  private getSelectedCasingIds(): string[] {
    const selectedItems: CasingSearchResult[] = this.tableRef?.selectedItems || [];
    return selectedItems.map((item) => item.workOrderLinePk).filter((casingId) => !!casingId);
  }

  private openTransferValidationModal(messages?: string[]): void {
    this.transferValidationMessages = messages?.length ? messages : [];
    this.transferValidationModal.open({
      id: 'transfer-validation-warning',
      width: 650,
      title: this.translationService.translate('warningText'),
      content: this.transferValidationContent,
      confirm: this.translationService.translate('okText')
    });
  }

  private openTransferConfirmationModal(): void {
    this.transferConfirmationModal.open({
      id: 'transfer-confirmation',
      width: 650,
      title: this.translationService.translate('transferCasingText'),
      content: this.transferConfirmationContent
    });
  }

  closeTransferConfirmationModal(): void {
    this.transferConfirmationModal.dialog.closeAll();
  }

  onTransferToNewWorkOrder(): void {
    this.transferConfirmationModal.dialog.closeAll();
    const selectedItems: CasingSearchResult[] = this.tableRef?.selectedItems || [];
    const transferredCasings = selectedItems.map((item) => ({
      casingId: item.casingId,
      processCode: item.processCode,
      customerName: item.customerName,
      productionCustomerName: item.productionCustomerName,
      casingMake: item.casingMake,
      casingMakePk: item.casingMakePk,
      casingSeries: item.casingSeries,
      casingSeriesPk: item.casingSeriesPk,
      casingSize: item.casingSize,
      casingSizePk: item.casingSizePk,
      treadDesc: item.treadDesc,
      treadDescPk: item.treadDescPk,
      shippingLocation: item.shippingLocation,
      postMarker: item.postMarker,
      mfgDot: item.mfgDot,
      brandNum: item.brandNum
    }));

    this.router.navigate(['/home/work-orders/transfer-to-new-wo'], {
      state: {
        transferredCasings
      }
    });
  }

  onTransferToExistingWorkOrder(): void {
    this.transferConfirmationModal.dialog.closeAll();
    this.store.dispatch(new UpdateSelectedCasingsAction(this.tableRef?.selectedItems || []));
    this.router.navigate(['/home/search-casings/transfer-casings']);
  }

  goBack(): void {
    this.router.navigate(['/home/search-casings']);
  }

  hasSelection(): boolean {
    return this.tableRef?.selectedItems?.length > 0;
  }

  getSelectedCount(): number {
    return this.tableRef?.selectedItems?.length || 0;
  }

  getDisplayedTotalCountLabel(): string {
    const totalCount = this.totalRecords || 0;
    if (!totalCount || this.currentPageSize <= 0 || this.currentPage <= 0) {
      return `0 - 0 of ${totalCount}`;
    }

    const startCount = (this.currentPage - 1) * this.currentPageSize + 1;
    const visibleCount = Math.min(this.currentPage * this.currentPageSize, totalCount);
    return `${startCount} - ${visibleCount} of ${totalCount}`;
  }

  onDelete(): void {
    if (!this.hasSelection()) {
      return;
    }

    this.dialog
      .open(SharedDeleteModalComponent, {
        data: {
          title: this.translationService.translate('deleteWorkOrderLineText'),
          messageTitle: this.translationService.translate('warningText'),
          message: this.translationService.translate('deleteWorkOrderLineDialogText'),
          buttonText: this.translationService.translate('yesDeleteText')
        }
      })
      .afterClosed()
      .subscribe((result) => {
        if (result === true) {
          const selectedItems: CasingSearchResult[] = this.tableRef?.selectedItems || [];

          // Prepare list of items to delete (ensure required ids present)
          const itemsToDelete = selectedItems.map((it) => it.workOrderLinePk).filter((id) => !!id);
          if (itemsToDelete.length === 0) return;

          this.workOrderService.markDeleteAndSaveCasingsBulk(this.dealer.id, itemsToDelete, this.currentUserName).subscribe({
            next: (resp) => {
              const success = resp?.data?.isSuccess ?? resp?.isSuccess ?? true;
              if (success) {
                this.alertService.showAlert({
                  type: AlertType.success,
                  content: this.translationService.translate('deleteSuccessfulText')
                } as Alert);
              } else {
                console.error('Bulk delete reported failure:', resp);
                this.alertService.showAlert({
                  type: AlertType.strong,
                  content: this.translationService.translate('deleteOperationFailedText')
                } as Alert);
              }
              this.cachedPages.clear();
              this.tableRef?.resetFilters();
            },
            error: (err) => {
              console.error('Bulk delete failed:', err);
              this.alertService.showAlert({
                type: AlertType.strong,
                content: this.translationService.translate('deleteOperationFailedText')
              } as Alert);
            }
          });
        }
      });
  }

  canDeleteSelection(): boolean {
    const selectedItems: CasingSearchResult[] = this.tableRef?.selectedItems || [];
    return selectedItems.length > 0 && selectedItems.every((item) => isDeletableCasingStatus(item.casingStatus));
  }

  onTransfer(): void {
    if (!this.canTransferSelection()) {
      if (this.hasSelection()) {
        this.openTransferValidationModal();
      }
      return;
    }

    if (!this.hasOnlyTransferEligibleSelection()) {
      this.openTransferValidationModal();
      return;
    }

    const casingIds = this.getSelectedCasingIds();
    if (!casingIds.length) {
      this.openTransferValidationModal(['No eligible casing IDs were found for transfer.']);
      return;
    }

    this.casingSearchService.transferCasings(casingIds).subscribe({
      next: (response: TransferCasingsResponse) => {
        const message = response?.data?.message?.trim();
        if (message === this.POS_EXPORT_WAITING_MESSAGE) {
          const responseCasingIds = response?.data?.casingIds || [];
          const formattedMessage = responseCasingIds.length
            ? `The selected casing ${responseCasingIds.join(', ')} is not ready to be transferred. please try again later`
            : 'The selected casing is not ready to be transferred. please try again later';
          this.openTransferValidationModal([formattedMessage]);
          return;
        }

        this.openTransferConfirmationModal();
        return;
      },
      error: () => {
        this.alertService.showAlert({
          type: AlertType.strong,
          content: 'Transfer operation failed.'
        } as Alert);
      }
    });
  }

  onCasingIdClick(item: CasingSearchResult): void {
    if (!item.workOrderHeaderPk || !item.workOrderLinePk) {
      console.warn('Missing required navigation parameters:');
      return;
    }

    // Persist current page context before opening casing/work-order line details.
    this.savePaginationState(item.casingId);
    this.loadingCasingId = item.casingId;
    this.enhancedCasingNavigationService.navigateToWorkOrderLineDetails(item);
  }

  onWorkOrderNumClick(item: CasingSearchResult): void {
    if (!item) {
      console.warn('Missing required navigation parameter: workOrderHeaderPk');
      return;
    }
    // Persist current page context before opening work-order details.
    this.savePaginationState(item.casingId);
    this.loadingWorkOrderNum = item.workOrderNum;
    this.enhancedCasingNavigationService.navigateToWorkOrderDetails(item);
  }

  openExportModal(): void {
    // Include every column (visible and hidden) in the export modal, all selected by default.
    const allKeys = this.columnOrder?.length ? this.columnOrder : Object.keys(this.canonicalColumns);
    const columns: ExportColumnDef[] = allKeys.map((key) => ({
      key,
      label: this.canonicalColumns[key]?.header ?? this.columnNames[key] ?? key,
      selected: true
    }));
    const totalRecords = this.totalRecords;
    const dataFetcher = (): Observable<ExportResponse> => {
      const criteria = this.store.selectSnapshot(CasingSearchState.searchCriteria);
      if (!criteria) {
        return of({ data: [] });
      }
      return this.casingSearchService
        .searchCasings({
          ...normalizeMultiSelectCriteria(criteria),
          page: 0,
          size: Math.min(totalRecords, 10000),
          sortBy: 'casingId',
          sortDir: 'ASC'
        })
        .pipe(
          map((response: PaginatedCasingSearchResponse) => ({ data: response.casingSearchResults as Record<string, any>[] })),
          catchError(() => of({ data: [] }))
        );
    };
    const data: ExportModalData = { columns, dataFetcher, fileNamePrefix: 'casing-export', maxExportRows: 10000 };
    this.dialog.open(ExportModalComponent, { data, width: '480px' });
  }

  getStatusLabel(status: string): string {
    if (!status) return '-';

    const statusLabels: { [key: string]: string } = {
      EDITABLE: this.translationService.translate('editableText'),
      INCOMPLETE: this.translationService.translate('incompleteText'),
      DELETED: this.translationService.translate('deletedText'),
      COMPLETE: this.translationService.translate('completeText'),
      ADJUSTMENT: this.translationService.translate('adjustmentText'),
      RECEIVED: this.translationService.translate('receivedText'),
      REJECTED: this.translationService.translate('rejectedText'),
      SHIPPED: this.translationService.translate('shippedText'),
      SHOP_FLOOR: this.translationService.translate('shopFloorText'),
      POSTED: this.translationService.translate('postedText'),
      TRANSFERRED: this.translationService.translate('transferredText'),
      RELEASED: this.translationService.translate('releasedText'),
      STOCK: this.translationService.translate('stockText'),
      SERVICE: this.translationService.translate('serviceText'),
      REJECT_PENDING: this.translationService.translate('rejectPendingText'),
      REJECT_FOR_ADMIN: this.translationService.translate('rejectForAdminText'),
      CASING_COMPLETION: this.translationService.translate('casingCompletionText'),
      IN_STOCK: this.translationService.translate('inStockText')
    };

    return statusLabels[status] || status;
  }

  getCasingStatusTooltip(status: string): string {
    const normalizedStatus = this.normalizeCasingStatus(status);
    const tooltipKeys: Record<string, string> = {
      EDITABLE: 'casingStatusEditableTooltipText',
      INCOMPLETE: 'casingStatusIncompleteTooltipText',
      RELEASED: 'casingStatusReleasedTooltipText',
      SHOP_FLOOR: 'casingStatusShopFloorTooltipText',
      REJECT_FOR_ADMIN: 'casingStatusRejectForAdminTooltipText',
      REJECT_PENDING: 'casingStatusRejectPendingTooltipText',
      DELETED: 'casingStatusDeletedTooltipText',
      CASING_COMPLETION: 'casingStatusCasingCompletionTooltipText',
      COMPLETE: 'casingStatusCompletedTooltipText',
      POSTED: 'casingStatusPostedTooltipText'
    };

    const tooltipKey = tooltipKeys[normalizedStatus];
    return tooltipKey ? this.translationService.translate(tooltipKey) : '';
  }

  getStatusClass(status: string): string {
    if (!status) return '';

    const statusClasses: { [key: string]: string } = {
      // Success/Complete states - Green
      COMPLETE: 'valid',
      SHIPPED: 'valid',
      POSTED: 'valid',

      // Active/In Progress states - Blue/Primary
      EDITABLE: 'primary',
      RECEIVED: 'primary',
      RELEASED: 'primary',
      STOCK: 'primary',
      SERVICE: 'primary',
      CASING_COMPLETION: 'primary',

      // Warning/Pending states - Yellow
      INCOMPLETE: 'yellow',
      ADJUSTMENT: 'yellow',
      TRANSFERRED: 'yellow',
      REJECT_PENDING: 'yellow',
      IN_STOCK: 'yellow',

      // Error/Problem states - Red/Warn
      DELETED: 'warn',
      REJECTED: 'warn',
      REJECT_FOR_ADMIN: 'warn'
    };

    const normalizedStatus = status.trim().toUpperCase();
    return statusClasses[normalizedStatus] || '';
  }

  formatCreationDate(dateString: string | undefined): string {
    if (!dateString) return '-';

    // First try to parse using dateAdapter (for user-entered dates)
    let parsedDate = this.dateAdapter.parse(dateString);

    // If parsing fails, try ISO 8601 format (for API responses like "2026-05-12T09:19:30Z")
    if (!parsedDate) {
      try {
        const momentDate = moment(dateString, moment.ISO_8601, true);
        if (momentDate.isValid()) {
          parsedDate = momentDate.toDate();
        }
      } catch {
        // Silent fail, will return '-' below
        parsedDate = null;
      }
    }

    if (!parsedDate) return '-';
    return this.dateAdapter.format(parsedDate, this.dateAdapter['currentFormat'] || 'DD/MM/YYYY');
  }

  onDeleteClick(workOrderLine: CasingSearchResult) {
    this.dialog
      .open(SharedDeleteModalComponent, {
        data: {
          title: this.translationService.translate('deleteWorkOrderLineText'),
          messageTitle: this.translationService.translate('warningText'),
          message: this.translationService.translate('deleteWorkOrderLineDialogText'),
          buttonText: this.translationService.translate('yesDeleteText')
        }
      })
      .afterClosed()
      .subscribe((result) => {
        if (result === true) {
          this.workOrderService
            .markDeleteAndSaveCasing(this.dealer.id, workOrderLine.workOrderHeaderPk, workOrderLine.workOrderLinePk, this.currentUserName)
            .subscribe({
              next: () => {
                this.alertService.showAlert({
                  type: AlertType.success,
                  content: this.translationService.translate('deleteSuccessfulText')
                } as Alert);
                this.cachedPages.clear();
                this.tableRef?.resetFilters();
              },
              error: () =>
                this.alertService.showAlert({
                  type: AlertType.strong,
                  content: this.translationService.translate('deleteOperationFailedText')
                } as Alert)
            });
        }
      });
  }
}
